/*
 * Copyright (c) 2026 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import {Component, input, signal} from '@angular/core';
import {ServerPagingController} from './server-paging-controller';

/** How far each arrow key moves the page-size highlight. */
const ARROW_KEY_STEPS: Readonly<Record<string, number>> = {ArrowDown: 1, ArrowUp: -1};

/**
 * AG Grid's paging panel, drawn here because a server-paged grid holds one page and AG Grid would count only that.
 * Reports what {@link ServerPagingController} heard from the server. Content projected into it sits at the left,
 * where AG Grid's own panel leaves room for custom buttons.
 */
@Component({
    selector: 'app-server-paging-panel',
    templateUrl: './server-paging-panel.component.html',
    styleUrls: ['./server-paging-panel.component.scss'],
    standalone: false
})
export class ServerPagingPanelComponent {
    private static instanceCount = 0;

    readonly paging = input.required<ServerPagingController<unknown>>();
    /** Prefix for the page-size list's element ids, unique to this panel so two panels on a page never share one. */
    readonly pageSizeListId = `server-paging-page-size-${ServerPagingPanelComponent.nextInstanceNumber()}`;
    /** Whether the page-size list is showing. */
    readonly pageSizePickerOpen = signal(false);
    /** Which size the open list has marked, so the arrow keys can move through it before one is chosen. */
    readonly pageSizePickerHighlight = signal(0);

    changePageSize(pageSize: number): void {
        this.pageSizePickerOpen.set(false);
        this.paging().changePageSize(pageSize);
    }

    togglePageSizePicker(): void {
        if (!this.pageSizePickerOpen()) {
            this.pageSizePickerHighlight.set(Math.max(0, this.paging().pageSizeOptions
                                                              .indexOf(this.paging().pageSize())));
        }
        this.pageSizePickerOpen.update(open => !open);
    }

    /** The arrows move through the sizes without loading any; only Enter or Space asks for the highlighted one. */
    onPageSizePickerKeyDown(event: KeyboardEvent): void {
        if (event.key === 'Escape' || event.key === 'Tab') {
            this.pageSizePickerOpen.set(false);
            return;
        }
        const step = ARROW_KEY_STEPS[event.key] ?? 0;
        const chooses = (event.key === 'Enter' || event.key === ' ');
        if (step === 0 && !chooses) {
            return;
        }
        event.preventDefault();
        if (!this.pageSizePickerOpen()) {
            this.togglePageSizePicker();
        } else if (chooses) {
            this.changePageSize(this.paging().pageSizeOptions[this.pageSizePickerHighlight()]);
        } else {
            const last = this.paging().pageSizeOptions.length - 1;
            this.pageSizePickerHighlight.update(at => Math.min(Math.max(at + step, 0), last));
        }
    }

    private static nextInstanceNumber(): number {
        ServerPagingPanelComponent.instanceCount++;
        return ServerPagingPanelComponent.instanceCount;
    }

    /** Closes the list when focus leaves the picker altogether, as clicking elsewhere on the page does. */
    onPageSizePickerFocusOut(event: FocusEvent): void {
        const movedTo = event.relatedTarget as HTMLElement | null;
        if (!movedTo?.closest('.ag-paging-page-size')) {
            this.pageSizePickerOpen.set(false);
        }
    }
}
